import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import sessionManager from './utils/sessionManager';
import Logo from './components/Logo';
import API_CONFIG from './config';
import './ProfilePage.css';

const ProfilePage = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [creditReportData, setCreditReportData] = useState(null);
  

  
  // Profile data
  const [profile, setProfile] = useState({
    phone: '',
    firstName: '',
    lastName: '',
    email: '',
    role: 'tenant',
    photo: '',
    officeName: ''
  });
  
  // Extended tenant profile data
  const [tenantProfile, setTenantProfile] = useState({
    moveInDate: '',
    occupation: '',
    monthlyIncome: '',
    phoneNumber: '',
    pets: null,
    smoking: null,
    creditScore: '',
    rentalBudget: '',
    numAdults: 1,
    numKids: 0,
    status: 'looking',
    durationOfStay: ''
  });
  
  // Property preferences
  const [preferences, setPreferences] = useState({
    city: '',
    areas: [],
    bedrooms: [],
    propertyTypes: [],
    furnished: [],
    rentalTerms: [],
    priceMin: 1000,
    priceMax: 3000,
    occupants: 2
  });
  

  
  // Filter options from backend
  const [filterOptions, setFilterOptions] = useState({
    cities: [],
    areas: [],
    propertyTypes: ['Basement', 'Private Room', 'Shared Room', 'House', 'Apartment', 'Condo'],
    bedrooms: ['Studio', '1-bedroom', '2-bedrooms', '3-bedrooms', '4+ bedrooms'],
    furnished: ['Yes', 'No'],
    rentalTerms: ['Daily', 'Weekly', 'Monthly', 'Yearly']
  });

  // Check authentication
  useEffect(() => {
    const authenticated = sessionManager.isAuthenticated();
    if (!authenticated) {
      navigate('/');
      return;
    }
    
    const sessionData = sessionManager.getUserInfo();
    setProfile(prev => ({
      ...prev,
      phone: sessionData?.phoneNumber || '',
      role: sessionData?.userRole || 'tenant'
    }));
    
    loadUserProfile();
    loadFilterOptions();
    loadCreditReport();
  }, [navigate]);

  const loadCreditReport = async () => {
    try {
      // Check if user has credit reports available
      const sessionData = sessionManager.getUserInfo();
      if (!sessionData?.authToken) return;
      
      const resp = await fetch(`${API_CONFIG.BASE_URL}${API_CONFIG.ENDPOINTS.CREDIT_REPORTS_LIST}`, {
        headers: {
          'Authorization': `Bearer ${sessionData.authToken}`
        }
      });
      const data = await resp.json();
      
      if (data.success && data.reports && data.reports.length > 0) {
        // User has credit reports - show the "View Credit Report" option
        const latestReport = data.reports[0]; // Most recent report
        setCreditReportData({
          available: true,
          report_id: latestReport.report_id,
          credit_score: latestReport.credit_score,
          report_date: latestReport.report_date,
          expiry_date: latestReport.expiry_date,
          is_expired: latestReport.is_expired
        });
      }
    } catch (error) {
      console.error('Error loading credit report list:', error);
    }
  };

  const loadUserProfile = async () => {
    try {
      setLoading(true);
      
      // Get user preferences from the existing backend endpoint
      const preferencesResponse = await fetch(`${API_CONFIG.BASE_URL}${API_CONFIG.ENDPOINTS.GET_USER_PREFERENCES}`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${sessionManager.getUserInfo()?.authToken}`
        }
      });

      if (preferencesResponse.ok) {
        const data = await preferencesResponse.json();
        if (data.success) {
          // Update preferences with backend data
          setPreferences(prev => ({
            ...prev,
            bedrooms: data.preferences?.bedrooms || [],
            propertyTypes: data.preferences?.property_types || [],
            furnished: data.preferences?.furnished_options || [],
            rentalTerms: data.preferences?.rental_terms || [],
            occupants: data.preferences?.occupants || 2,
            areas: data.preferences?.neighborhoods || []
          }));
          
          // Extract price range if available
          if (data.preferences?.price_range) {
            const priceRange = data.preferences.price_range.replace(/\$/g, '').split('-');
            if (priceRange.length === 2) {
              setPreferences(prev => ({
                ...prev,
                priceMin: parseInt(priceRange[0]) || 1000,
                priceMax: parseInt(priceRange[1]) || 3000
              }));
            }
          }

          // Load profile data if available
          if (data.profile) {
            setProfile(prev => ({
              ...prev,
              firstName: data.profile.firstName || '',
              lastName: data.profile.lastName || '',
              email: data.profile.email || '',
              role: data.profile.role || 'tenant',
              officeName: data.profile.officeName || '',
              photo: data.profile.photo || null
            }));
          }
        }
      }
      
      // Get extended tenant profile data
      const tenantProfileResponse = await fetch(`${API_CONFIG.BASE_URL}${API_CONFIG.ENDPOINTS.GET_TENANT_PROFILE}`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${sessionManager.getUserInfo()?.authToken}`
        }
      });

      if (tenantProfileResponse.ok) {
        const tenantData = await tenantProfileResponse.json();
        if (tenantData.success && tenantData.profile) {
          setTenantProfile(prev => ({
            ...prev,
            moveInDate: tenantData.profile.moveInDate || '',
            occupation: tenantData.profile.occupation || '',
            monthlyIncome: tenantData.profile.monthlyIncome || '',
            phoneNumber: tenantData.profile.phoneNumber || '',
            pets: tenantData.profile.pets,
            smoking: tenantData.profile.smoking,
            creditScore: tenantData.profile.creditScore || '',
            rentalBudget: tenantData.profile.rentalBudget || '',
            numAdults: tenantData.profile.numAdults || 1,
            numKids: tenantData.profile.numKids || 0,
            status: tenantData.profile.status || 'looking',
            durationOfStay: tenantData.profile.durationOfStay || ''
          }));
        }
      } else {
        const errorText = await tenantProfileResponse.text();
        console.error('Failed to load tenant profile:', errorText);
      }
    } catch (error) {
      console.error('Error loading profile:', error);
      setError('Failed to load profile data');
    } finally {
      setLoading(false);
    }
  };

  const loadFilterOptions = async () => {
    try {
      // Load cities and areas from the same endpoint used in HomePage
      const response = await fetch('https://nk2lgsxkwf.execute-api.us-east-1.amazonaws.com/prod/listing-details-pivot');
      if (response.ok) {
        const data = await response.json();
        if (data.success && data.data?.filter_options) {
          // Handle neighborhoods from backend
          const neighborhoods = data.data.filter_options.neighborhoods || [];
          
          // If neighborhoods have ID and name properties, use them directly
          if (neighborhoods.length > 0 && typeof neighborhoods[0] === 'object') {
            setFilterOptions(prev => ({
              ...prev,
              cities: data.data.filter_options.cities || [],
              areas: neighborhoods
            }));
          } else {
            // Fallback: if neighborhoods are just strings, convert to objects
            setFilterOptions(prev => ({
              ...prev,
              cities: data.data.filter_options.cities || [],
              areas: neighborhoods.map((name, index) => ({ id: index + 1, name }))
            }));
          }
        }
      }
    } catch (error) {
      console.error('Error loading filter options:', error);
    }
  };

  const handleCityChange = async (event) => {
    const city = event.target.value;
    setPreferences(prev => ({
      ...prev,
      city,
      areas: [] // Reset areas when city changes
    }));
    
    // Load areas for selected city
    if (city) {
      try {
        const response = await fetch(`https://nk2lgsxkwf.execute-api.us-east-1.amazonaws.com/prod/city-areas?city=${encodeURIComponent(city)}`);
        if (response.ok) {
          const data = await response.json();
          if (data.success && data.data) {
            setFilterOptions(prev => ({
              ...prev,
              areas: data.data.areas || []
            }));
          }
        }
      } catch (error) {
        console.error('Error loading areas:', error);
      }
    } else {
      setFilterOptions(prev => ({ ...prev, areas: [] }));
    }
  };

  const handleMultiSelect = (field, value) => {
    setPreferences(prev => {
      const currentValues = prev[field] || [];
      
      // For areas, work with IDs instead of names
      if (field === 'areas') {
        // value should be the neighborhood ID
        const newValues = currentValues.includes(value)
          ? currentValues.filter(v => v !== value)
          : [...currentValues, value];
        return { ...prev, [field]: newValues };
      } else {
        // For other fields, work with values as before
        const newValues = currentValues.includes(value)
          ? currentValues.filter(v => v !== value)
          : [...currentValues, value];
        return { ...prev, [field]: newValues };
      }
    });
  };

  const saveProfile = async () => {
    try {
      setSaving(true);
      setError('');
      setSuccess('');

      const sessionData = sessionManager.getUserInfo();
      if (!sessionData?.authToken) {
        setError('Authentication required');
        return;
      }

      // Save basic profile information first
      const profileSaveResponse = await fetch(`${API_CONFIG.BASE_URL}${API_CONFIG.ENDPOINTS.UPDATE_USER_PROFILE}`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${sessionManager.getUserInfo()?.authToken}`
        },
        body: JSON.stringify({
          firstName: profile.firstName,
          lastName: profile.lastName,
          role: profile.role,
          email: profile.email,
          officeName: profile.officeName,
          photo: profile.photo
        })
      });

      if (!profileSaveResponse.ok) {
        throw new Error('Failed to save profile information');
      }

      // Save tenant profile data
      const tenantProfileSaveResponse = await fetch(`${API_CONFIG.BASE_URL}${API_CONFIG.ENDPOINTS.UPDATE_TENANT_PROFILE}`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${sessionManager.getUserInfo()?.authToken}`
        },
        body: JSON.stringify(tenantProfile)
      });

      if (!tenantProfileSaveResponse.ok) {
        const errorText = await tenantProfileSaveResponse.text();
        console.error('Tenant profile save failed:', errorText);
        throw new Error(`Failed to save tenant profile information: ${errorText}`);
      }

      // Save preferences using existing backend endpoints
      const saveEndpoints = [
        {
          endpoint: `${API_CONFIG.BASE_URL}${API_CONFIG.ENDPOINTS.SAVE_PROPERTY_TYPE_PREFERENCES}`,
          data: { property_types: preferences.propertyTypes }
        },
        {
          endpoint: `${API_CONFIG.BASE_URL}${API_CONFIG.ENDPOINTS.SAVE_BEDROOM_PREFERENCES}`,
          data: { bedrooms: preferences.bedrooms }
        },
        {
          endpoint: `${API_CONFIG.BASE_URL}${API_CONFIG.ENDPOINTS.SAVE_NEIGHBORHOOD_PREFERENCES}`,
          data: { neighborhoods: preferences.areas }
        },
        {
          endpoint: `${API_CONFIG.BASE_URL}${API_CONFIG.ENDPOINTS.SAVE_FURNISHED_PREFERENCES}`,
          data: { furnished_options: preferences.furnished }
        },
        {
          endpoint: `${API_CONFIG.BASE_URL}${API_CONFIG.ENDPOINTS.SAVE_RENTAL_TERM_PREFERENCES}`,
          data: { rental_terms: preferences.rentalTerms }
        },
        {
          endpoint: `${API_CONFIG.BASE_URL}${API_CONFIG.ENDPOINTS.UPDATE_TENANT_PREFERENCES}`,
          data: { 
            occupants: preferences.occupants,
            min_price: preferences.priceMin,
            max_price: preferences.priceMax
          }
        }
      ];

      // Execute all save operations
      for (const { endpoint, data } of saveEndpoints) {
        const response = await fetch(endpoint, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${sessionData.authToken}`
          },
          body: JSON.stringify(data)
        });

        if (!response.ok) {
          throw new Error(`Failed to save preferences: ${response.status}`);
        }
      }

      setSuccess('Profile updated successfully!');
      setTimeout(() => setSuccess(''), 3000);
      
    } catch (error) {
      console.error('Error saving profile:', error);
      setError('Failed to save profile. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  const getRoleIcon = () => {
    switch (profile.role) {
      case 'tenant': return '🏠';
      case 'landlord': return '🏢';
      case 'agent': return '👔';
      case 'property_manager': return '📋';
      default: return '👤';
    }
  };



  if (loading) {
    return (
      <div className="profile-page">
        <div className="profile-loading">
          <div className="loading-spinner"></div>
          <p>Loading profile...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="profile-page">
      {/* Header */}
      <header className="profile-header">
        <div className="header-container">
          <Logo size="medium" showText={true} variant="dark" onClick={() => navigate('/')} />
          <div className="header-actions">
            <button 
              onClick={() => navigate('/chat')} 
              className="back-btn"
              title="Back to Chat"
            >
              💬 Chat
            </button>
            <button 
              onClick={() => navigate('/')} 
              className="home-btn"
              title="Back to Home"
            >
              🏠 Home
            </button>
          </div>
        </div>
      </header>

      <div className="profile-container">
        <div className="profile-content">
          <h1 className="profile-title">
            {getRoleIcon()} Profile Settings
          </h1>

          {error && (
            <div className="alert alert-error">
              {error}
            </div>
          )}

          {success && (
            <div className="alert alert-success">
              {success}
            </div>
          )}

          {/* Basic Information */}
          <section className="profile-section">
            <h2>Basic Information</h2>
            
            <div className="form-grid">
              <div className="form-group">
                <label>Phone Number (View Only)</label>
                <input
                  type="text"
                  value={profile.phone}
                  disabled
                  className="input-disabled"
                />
                <small>Your phone number cannot be changed</small>
              </div>

              <div className="form-group">
                <label>Role</label>
                <select
                  value={profile.role}
                  onChange={(e) => setProfile(prev => ({ ...prev, role: e.target.value }))}
                  className="select-input"
                >
                  <option value="tenant">🏠 Tenant</option>
                  <option value="landlord">🏢 Landlord</option>
                  <option value="agent">👔 Real Estate Agent</option>
                  <option value="property_manager">📋 Property Manager</option>
                </select>
              </div>

              <div className="form-group">
                <label>First Name (Optional)</label>
                <input
                  type="text"
                  value={profile.firstName}
                  onChange={(e) => setProfile(prev => ({ ...prev, firstName: e.target.value }))}
                  placeholder="Enter your first name"
                />
              </div>

              <div className="form-group">
                <label>Last Name (Optional)</label>
                <input
                  type="text"
                  value={profile.lastName}
                  onChange={(e) => setProfile(prev => ({ ...prev, lastName: e.target.value }))}
                  placeholder="Enter your last name"
                />
              </div>

              <div className="form-group">
                <label>Email (Optional)</label>
                <input
                  type="email"
                  value={profile.email}
                  onChange={(e) => setProfile(prev => ({ ...prev, email: e.target.value }))}
                  placeholder="Enter your email address"
                />
              </div>

              <div className="form-group">
                <label>Office Name (Optional)</label>
                <input
                  type="text"
                  value={profile.officeName}
                  onChange={(e) => setProfile(prev => ({ ...prev, officeName: e.target.value }))}
                  placeholder="Enter your office/company name"
                />
              </div>
            </div>
          </section>

          {/* Extended Profile Information */}
          <section className="profile-section">
            <h2>Extended Profile Information</h2>
            
            <div className="form-grid">
              <div className="form-group">
                <label>Move-in Date</label>
                <input
                  type="date"
                  value={tenantProfile.moveInDate}
                  onChange={(e) => setTenantProfile(prev => ({ ...prev, moveInDate: e.target.value }))}
                />
              </div>

              <div className="form-group">
                <label>Rental Term Duration</label>
                <select
                  value={tenantProfile.durationOfStay}
                  onChange={(e) => setTenantProfile(prev => ({ ...prev, durationOfStay: e.target.value }))}
                  className="select-input"
                >
                  <option value="">Select Duration</option>
                  <option value="1-3 months">1-3 months</option>
                  <option value="3-6 months">3-6 months</option>
                  <option value="6-12 months">6-12 months</option>
                  <option value="12+ months">12+ months</option>
                </select>
              </div>

              <div className="form-group">
                <label>Occupation</label>
                <input
                  type="text"
                  value={tenantProfile.occupation}
                  onChange={(e) => setTenantProfile(prev => ({ ...prev, occupation: e.target.value }))}
                  placeholder="Enter your occupation"
                />
              </div>

              <div className="form-group">
                <label>Monthly Income (CAD)</label>
                <input
                  type="number"
                  value={tenantProfile.monthlyIncome}
                  onChange={(e) => setTenantProfile(prev => ({ ...prev, monthlyIncome: e.target.value }))}
                  placeholder="Enter monthly income"
                  min="0"
                  step="100"
                />
              </div>

              <div className="form-group">
                <label>Pets</label>
                <select
                  value={tenantProfile.pets === null ? '' : (tenantProfile.pets ? 'yes' : 'no')}
                  onChange={(e) => setTenantProfile(prev => ({ 
                    ...prev, 
                    pets: e.target.value === '' ? null : e.target.value === 'yes' 
                  }))}
                  className="select-input"
                >
                  <option value="">Select Option</option>
                  <option value="yes">Yes</option>
                  <option value="no">No</option>
                </select>
              </div>

              <div className="form-group">
                <label>Smoking</label>
                <select
                  value={tenantProfile.smoking === null ? '' : (tenantProfile.smoking ? 'yes' : 'no')}
                  onChange={(e) => setTenantProfile(prev => ({ 
                    ...prev, 
                    smoking: e.target.value === '' ? null : e.target.value === 'yes' 
                  }))}
                  className="select-input"
                >
                  <option value="">Select Option</option>
                  <option value="yes">Yes</option>
                  <option value="no">No</option>
                </select>
              </div>

              <div className="form-group credit-report-section">
                <label>Credit Report</label>
                <div className="credit-report-options">
                                  {creditReportData?.available && !creditReportData?.is_expired && (
                  <div className="credit-option credit-report-available">
                    <div className="report-status">
                      <span className="status-icon">✅</span>
                      <span className="status-text">Credit Report Available</span>
                      <span className="report-date">
                        Score: {creditReportData.credit_score || 'Processing...'}
                        <br />
                        Generated: {creditReportData.report_date ? new Date(creditReportData.report_date).toLocaleDateString() : 'Recent'}
                        <br />
                        🔒 Encrypted Storage
                      </span>
                    </div>
                    <button
                      type="button"
                      className="view-report-btn"
                      onClick={() => navigate('/credit-report')}
                    >
                      📊 View Credit Report
                    </button>
                  </div>
                )}
                
                {creditReportData?.available && creditReportData?.is_expired && (
                  <div className="credit-option credit-report-expired">
                    <div className="report-status">
                      <span className="status-icon">⚠️</span>
                      <span className="status-text">Credit Report Expired</span>
                      <span className="report-date">
                        Expired: {creditReportData.expiry_date ? new Date(creditReportData.expiry_date).toLocaleDateString() : 'Recently'}
                      </span>
                    </div>
                    <button
                      type="button"
                      className="expired-report-btn"
                      onClick={() => navigate('/credit-pull')}
                    >
                      🔄 Pull New Report
                    </button>
                  </div>
                )}
                  
                  <div className="credit-option">
                    <input
                      type="file"
                      id="credit-upload"
                      accept=".pdf,.jpg,.jpeg,.png"
                      style={{ display: 'none' }}
                      onChange={(e) => {
                        // Handle file upload logic here
                        const file = e.target.files[0];
                        if (file) {
                          console.log('Credit report file selected:', file.name);
                          // TODO: Implement file upload logic
                        }
                      }}
                    />
                    <button
                      type="button"
                      className="credit-upload-btn"
                      onClick={() => document.getElementById('credit-upload').click()}
                    >
                      📄 Upload Credit Report
                    </button>
                    <small>Upload PDF, JPG, or PNG (Max 5MB)</small>
                  </div>
                  
                  <div className="credit-divider">
                    <span>OR</span>
                  </div>
                  
                  <div className="credit-option">
                    <button 
                      type="button"
                      className="credit-pull-btn"
                      onClick={() => navigate('/credit-pull')}
                    >
                      🔍 Soft Pull Credit Report
                    </button>
                    <small>Get your credit report from Equifax</small>
                  </div>
                </div>
              </div>

              <div className="form-group">
                <label>Number of Adults</label>
                <input
                  type="number"
                  value={tenantProfile.numAdults}
                  onChange={(e) => setTenantProfile(prev => ({ 
                    ...prev, 
                    numAdults: parseInt(e.target.value) || 1 
                  }))}
                  min="1"
                  max="10"
                />
              </div>

              <div className="form-group">
                <label>Number of Children</label>
                <input
                  type="number"
                  value={tenantProfile.numKids}
                  onChange={(e) => setTenantProfile(prev => ({ 
                    ...prev, 
                    numKids: parseInt(e.target.value) || 0 
                  }))}
                  min="0"
                  max="10"
                />
              </div>
            </div>
          </section>

          {/* Property Preferences */}
          <section className="profile-section">
            <h2>Property Preferences</h2>
            
            {/* Location Preferences */}
            <div className="preference-group">
              <h3>📍 Location</h3>
              <div className="form-grid">
                <div className="form-group">
                  <label>City</label>
                  <select
                    value={preferences.city}
                    onChange={handleCityChange}
                    className="select-input"
                  >
                    <option value="">All Cities</option>
                    {filterOptions.cities.map(city => (
                      <option key={city} value={city}>{city}</option>
                    ))}
                  </select>
                </div>

                <div className="form-group">
                  <label>Areas/Neighborhoods</label>
                  <div className="multi-select">
                    {filterOptions.areas.length === 0 && !preferences.city ? (
                      <p className="no-options">Select a city first</p>
                    ) : filterOptions.areas.length === 0 ? (
                      <p className="no-options">No areas available</p>
                    ) : (
                      filterOptions.areas.map(area => {
                        // Handle both object and string formats
                        const areaId = typeof area === 'object' ? area.id : area;
                        const areaName = typeof area === 'object' ? area.name : area;
                        
                        return (
                          <label key={areaId} className="checkbox-label">
                            <input
                              type="checkbox"
                              checked={preferences.areas.includes(areaId)}
                              onChange={() => handleMultiSelect('areas', areaId)}
                            />
                            {areaName}
                          </label>
                        );
                      })
                    )}
                    {preferences.areas.length === 0 && (
                      <p className="default-text">Default: All areas</p>
                    )}
                  </div>
                </div>
              </div>
            </div>

            {/* Property Details */}
            <div className="preference-group">
              <h3>🏠 Property Details</h3>
              <div className="form-grid">
                <div className="form-group">
                  <label>Number of Bedrooms</label>
                  <div className="multi-select">
                    {filterOptions.bedrooms.map(bedroom => (
                      <label key={bedroom} className="checkbox-label">
                        <input
                          type="checkbox"
                          checked={preferences.bedrooms.includes(bedroom)}
                          onChange={() => handleMultiSelect('bedrooms', bedroom)}
                        />
                        {bedroom}
                      </label>
                    ))}
                    {preferences.bedrooms.length === 0 && (
                      <p className="default-text">Default: All bedroom types</p>
                    )}
                  </div>
                </div>

                <div className="form-group">
                  <label>Property Type</label>
                  <div className="multi-select">
                    {filterOptions.propertyTypes.map(type => (
                      <label key={type} className="checkbox-label">
                        <input
                          type="checkbox"
                          checked={preferences.propertyTypes.includes(type)}
                          onChange={() => handleMultiSelect('propertyTypes', type)}
                        />
                        {type}
                      </label>
                    ))}
                    {preferences.propertyTypes.length === 0 && (
                      <p className="default-text">Default: All property types</p>
                    )}
                  </div>
                </div>

                <div className="form-group">
                  <label>Furnished</label>
                  <div className="multi-select">
                    {filterOptions.furnished.map(option => (
                      <label key={option} className="checkbox-label">
                        <input
                          type="checkbox"
                          checked={preferences.furnished.includes(option)}
                          onChange={() => handleMultiSelect('furnished', option)}
                        />
                        {option}
                      </label>
                    ))}
                    {preferences.furnished.length === 0 && (
                      <p className="default-text">Default: Both furnished and unfurnished</p>
                    )}
                  </div>
                </div>

                <div className="form-group">
                  <label>Rental Term</label>
                  <div className="multi-select">
                    {filterOptions.rentalTerms.map(term => (
                      <label key={term} className="checkbox-label">
                        <input
                          type="checkbox"
                          checked={preferences.rentalTerms.includes(term)}
                          onChange={() => handleMultiSelect('rentalTerms', term)}
                        />
                        {term}
                      </label>
                    ))}
                    {preferences.rentalTerms.length === 0 && (
                      <p className="default-text">Default: All rental terms</p>
                    )}
                  </div>
                </div>
              </div>
            </div>

            {/* Budget and Occupancy */}
            <div className="preference-group">
              <h3>💰 Budget & Occupancy</h3>
              <div className="form-grid">
                <div className="form-group">
                  <label>Monthly Budget (CAD)</label>
                  <div className="price-range">
                    <div className="price-input">
                      <label>Min</label>
                      <input
                        type="number"
                        value={preferences.priceMin}
                        onChange={(e) => setPreferences(prev => ({ 
                          ...prev, 
                          priceMin: parseInt(e.target.value) || 0 
                        }))}
                        min="0"
                        step="50"
                      />
                    </div>
                    <span className="price-separator">to</span>
                    <div className="price-input">
                      <label>Max</label>
                      <input
                        type="number"
                        value={preferences.priceMax}
                        onChange={(e) => setPreferences(prev => ({ 
                          ...prev, 
                          priceMax: parseInt(e.target.value) || 0 
                        }))}
                        min="0"
                        step="50"
                      />
                    </div>
                  </div>
                </div>

                <div className="form-group">
                  <label>Number of Occupants</label>
                  <input
                    type="number"
                    value={preferences.occupants}
                    onChange={(e) => setPreferences(prev => ({ 
                      ...prev, 
                      occupants: parseInt(e.target.value) || 1 
                    }))}
                    min="1"
                    max="10"
                  />
                </div>
              </div>
            </div>
          </section>

          {/* Save Button */}
          <div className="profile-actions">
            <button
              onClick={saveProfile}
              disabled={saving}
              className="save-btn"
            >
              {saving ? (
                <>
                  <div className="btn-spinner"></div>
                  Saving...
                </>
              ) : (
                '💾 Save Profile'
              )}
            </button>
          </div>
        </div>
      </div>



    </div>
  );
};

export default ProfilePage; 